/**
 * Sessions dashboard section: tabbed list with create/edit collapse.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SessionsDataTable } from '@/components/sessions/inperson/SessionsDataTable';
import { CreateSessionView } from '@/components/sessions/inperson/CreateSessionView';
import { EditSessionView } from '@/components/sessions/inperson/EditSessionView';
import { SelectSessionTypeModal } from '@/components/sessions/inperson/SelectSessionTypeModal';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { useDetailsView } from '@/hooks/useDetailsView';
import { usePageActions } from '@/hooks/usePageActions';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { Select } from '@/components/forms/Select';
import {
  SESSION_IN_PERSON_TAB_COLORS as TAB_COLORS,
  SESSION_TYPE_CONFIG,
} from '@/components/sessions/session-configs';
import {
  SESSION_IN_PERSON_TAB_LABELS as TAB_LABELS,
  SESSION_TAB_TO_TYPES,
} from '@/constants/shared-catalog';
import { toSessionInPersonTab } from '@/utils/common-utils';
import {
  type SessionTab,
  type PlanificationType,
  type SessionGroup,
} from '@/types/inperson/session.types';
import { CalendarSyncModal } from '@/components/calendar/CalendarSyncModal';
import { CALENDAR_SYNC_ENABLED } from '@/config/features';
import { Button } from '@/components/forms/Button';
import { RefreshCw } from 'lucide-react';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link SessionsSection}. */
interface SessionsSectionProps {
  onSuccess?: () => void;
  layoutWidth?: ElementWidth;
}

/** Which form is mounted in the collapse wrapper (session, group, or information meeting). */
type ActiveForm =
  | 'create'
  | 'edit'
  | 'edit-group'
  | 'create-information'
  | 'edit-information'
  | null;

/** Session id currently open in the edit form, if any. */
type EditableSessionId = string | null;
/** Session group currently open in the edit form, if any. */
type EditableSessionGroup = SessionGroup | null;
/** Planification type chosen in the create-session picker. */
type SelectedPlanificationType = PlanificationType | null;

/** Session tabs shown in the in-person dashboard widget. */
const TAB_ORDER: SessionTab[] = ['tutoring', 'information-meeting'];

/** Dashboard widget: in-person sessions list with type picker. */
export const SessionsSection: React.FC<SessionsSectionProps> = ({
  onSuccess,
  layoutWidth = 'full',
}) => {
  const {
    canEditRow,
    canManageAttendance,
    canViewReport,
    canViewReportInformation,
    canExportCsv,
    canJoinInformationMeeting,
    creatableTypes: creatableTypesRaw,
  } = usePageActions('sessions');
  const { elements } = useInterfaceElements('sessions');
  const [activeTab, setActiveTab] = React.useState<SessionTab>('tutoring');
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editSessionId, setEditSessionId] = React.useState<EditableSessionId>(null);
  const [editGroup, setEditGroup] = React.useState<EditableSessionGroup>(null);
  const [planificationType, setPlanificationType] = React.useState<SelectedPlanificationType>(null);
  const [planificationModalOpen, setPlanificationModalOpen] = React.useState(false);
  const [syncOpen, setSyncOpen] = React.useState(false);

  function handleOpenSync(): void {
    setSyncOpen(true);
  }

  function handleCloseSync(): void {
    setSyncOpen(false);
  }
  const queryClient = useQueryClient();
  const { openRowId, openRow, closeRow } = useDetailsView();
  const sectionRef = React.useRef<HTMLDivElement>(null);

  const allowedTabIds = React.useMemo((): SessionTab[] => {
    const tabElements = elements.filter((el) => el.elementType === 'tab');
    const tabs: SessionTab[] =
      tabElements.length === 0
        ? TAB_ORDER
        : (tabElements
            .map((el) => toSessionInPersonTab(el.elementId))
            .filter((id): id is SessionTab => id !== null)
            .sort((a, b) => TAB_ORDER.indexOf(a) - TAB_ORDER.indexOf(b)) as SessionTab[]);
    return tabs;
  }, [elements]);

  /**
   * Types creatable from action-create-* RBAC elements (per-type).
   * Falls back to allowedTabIds when no action-create-* elements exist.
   */
  const creatableTabIds = React.useMemo((): SessionTab[] => {
    const TAB_IDS = new Set<SessionTab>(['tutoring', 'information-meeting']);
    if (creatableTypesRaw.length > 0) {
      return creatableTypesRaw.filter((id): id is SessionTab => TAB_IDS.has(id as SessionTab));
    }
    return allowedTabIds;
  }, [creatableTypesRaw, allowedTabIds]);

  const tabItems = React.useMemo(
    () =>
      allowedTabIds.map((id) => {
        const firstType = SESSION_TAB_TO_TYPES[id]?.[0];
        const Icon = firstType ? SESSION_TYPE_CONFIG[firstType]?.icon : undefined;
        return {
          id,
          label: TAB_LABELS[id],
          icon: Icon ? <Icon className="h-4 w-4" /> : undefined,
          color: TAB_COLORS[id],
        };
      }),
    [allowedTabIds],
  );

  const tabsNode = React.useMemo(
    () => (
      <Select
        value={activeTab}
        onChange={(id) => {
          closeForm();
          closeRow();
          setActiveTab(id as SessionTab);
        }}
        options={tabItems.map((tab) => ({
          value: tab.id,
          label: tab.label,
          icon: tab.icon,
          color: tab.color,
        }))}
        size="sm"
      />
    ),
    [tabItems, activeTab, closeRow],
  );

  /** Close the form and drop all edit / planification state. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditSessionId(null);
    setEditGroup(null);
    setPlanificationType(null);
  }, []);

  const handlePlanificationSelect = (type: PlanificationType) => {
    if (type === 'information_meeting') {
      setActiveForm('create-information');
      setPlanificationModalOpen(false);
      closeRow();
    } else {
      setPlanificationType(type);
      setPlanificationModalOpen(false);
      setActiveForm('create');
      setEditSessionId(null);
      closeRow();
    }
  };

  const handleEditSession = (sessionId: string, sessionType?: string) => {
    setEditSessionId(sessionId);
    setActiveForm(
      sessionType === 'information_meeting_in_person' || activeTab === 'information-meeting'
        ? 'edit-information'
        : 'edit',
    );
    closeRow();
  };

  const handleEditGroup = (group: SessionGroup) => {
    setEditGroup(group);
    setEditSessionId(null);
    setActiveForm('edit-group');
    closeRow();
  };

  /** Scroll the section to the form when it first opens. */
  const prevActiveFormRef = React.useRef(activeForm);
  React.useEffect(() => {
    if (prevActiveFormRef.current !== activeForm && activeForm !== null && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevActiveFormRef.current = activeForm;
  }, [activeForm]);

  const formContent = React.useMemo(() => {
    if (activeForm === 'create' && planificationType) {
      return (
        <CreateSessionView
          planificationType={planificationType}
          onClose={closeForm}
          onSuccess={() => {
            closeForm();
            void queryClient.invalidateQueries({ queryKey: ['sessions'] });
            void queryClient.invalidateQueries({ queryKey: ['session-groups'] });
            onSuccess?.();
          }}
        />
      );
    }
    if (activeForm === 'create-information') {
      return (
        <CreateSessionView
          kind="information_meeting"
          onClose={closeForm}
          onSuccess={() => {
            closeForm();
            void queryClient.invalidateQueries({ queryKey: ['sessions'] });
            onSuccess?.();
          }}
        />
      );
    }
    if (activeForm === 'edit' && editSessionId) {
      return (
        <EditSessionView
          key={`sessions-edit-${editSessionId}`}
          sessionId={editSessionId}
          onClose={closeForm}
          onSuccess={() => {
            closeForm();
            void queryClient.invalidateQueries({ queryKey: ['sessions'] });
            void queryClient.invalidateQueries({ queryKey: ['session-groups'] });
          }}
        />
      );
    }
    if (activeForm === 'edit-information' && editSessionId) {
      return (
        <EditSessionView
          key={`sessions-edit-information-${editSessionId}`}
          kind="information_meeting"
          sessionId={editSessionId}
          onClose={closeForm}
          onSuccess={() => {
            closeForm();
            void queryClient.invalidateQueries({ queryKey: ['sessions'] });
          }}
        />
      );
    }
    if (activeForm === 'edit-group' && editGroup) {
      return (
        <CreateSessionView
          editGroup={editGroup}
          onClose={closeForm}
          onSuccess={() => {
            closeForm();
            void queryClient.invalidateQueries({ queryKey: ['sessions'] });
            void queryClient.invalidateQueries({ queryKey: ['session-groups'] });
            onSuccess?.();
          }}
        />
      );
    }
    return null;
  }, [activeForm, planificationType, editSessionId, editGroup, queryClient, onSuccess, closeForm]);

  return (
    <div ref={sectionRef}>
      <CollapseReplaceWrapper mode="replace" isOpen={activeForm !== null} formContent={formContent}>
        <SessionsDataTable
          showCreateButton={true}
          onCreateSession={() => setPlanificationModalOpen(true)}
          layoutWidth={layoutWidth}
          isSection={true}
          expandedRowId={openRowId}
          onToggleRow={openRow}
          onCloseDetails={closeRow}
          onEditSession={handleEditSession}
          onEditGroup={handleEditGroup}
          activeTab={activeTab}
          allowedTabIds={allowedTabIds}
          toolbarLeft={tabsNode}
          toolbarExtra={
            CALENDAR_SYNC_ENABLED ? (
              <Button
                preset="section-action"
                icon={RefreshCw}
                tooltipText="Synchroniser"
                aria-label="Synchroniser"
                onClick={handleOpenSync}
              />
            ) : undefined
          }
          pageActions={{
            canEditRow,
            canManageAttendance,
            canViewReport,
            canViewReportInformation,
            canJoinInformationMeeting,
            canExportCsv,
          }}
        />
      </CollapseReplaceWrapper>
      <SelectSessionTypeModal
        open={planificationModalOpen}
        onClose={() => setPlanificationModalOpen(false)}
        onSelect={handlePlanificationSelect}
        allowedTabIds={creatableTabIds}
      />
      {CALENDAR_SYNC_ENABLED ? (
        <CalendarSyncModal open={syncOpen} onClose={handleCloseSync} lockedTypes={['session']} />
      ) : null}
    </div>
  );
};
